import {useTheme} from "../../../../../connectui/theme/ThemeProvider";
import {buildStyles} from "../../../../../connectui/component/buildStyles";
import Text from "../../../../../connectui/component/display/Text";
import React, {useEffect, useLayoutEffect, useMemo, useState} from "react";
import DropdownExpand from "../../../../../connectui/component/display/dropdown/DropdownExpand";
import Tabs from "../../../../../connectui/component/input/Tabs";
import Container from "../../../../../connectui/component/layout/Container";
import {useDispatch, useSelector} from "react-redux";
import {orderBookings} from "utilities/helperFunctions";
import {
  editBooking,
  setSelectedGuestBookings,
} from "redux/actions/bookingsActions";
import FlexContainer from "../../../../../connectui/component/layout/FlexContainer";
import ListingInfoCard from "../../guestdetails/reservation/ListingInfoCard";
import BoardingPassCard from "../../guestdetails/reservation/BoardingPassCard";
import ButtonIconText from "../../../../../connectui/component/input/button/ButtonIconText";
import SvgIcon from "../../../../../connectui/component/display/SvgIcon";
import ICONS from "../../../../../connectui/icons/iconMap";
import CustomFieldsCard from "../../guestdetails/reservation/CustomFieldsCard";
import AccessCodesCard from "../../guestdetails/reservation/AccessCodesCard";
import UpsellsCard from "../../guestdetails/reservation/UpsellsCard";
import FeesCard from "../../guestdetails/reservation/FeesCard";
import ChipText from "../../../../../connectui/component/display/chip/ChipText";
import ActionApproveDeclineCard from "../../guestdetails/reservation/ActionApproveDeclineCard";
import {getGuestKeycardLink} from "../../../../../redux/actions/accountsActions";
import {createAction} from "../../../../../redux/actions/experiencesActions";
import {isMobile} from "react-device-detect";
import {
  editGuest,
  getInboxData,
} from "../../../../../redux/actions/guestsActions";
import {useTranslation} from "react-i18next";

const useStyles = (theme) =>
  buildStyles({
    container: {
      borderRadius: theme.spacing(2, 2, 4, 4),
      width: "100%",
      boxShadow: theme.shadows.md,
      backgroundColor: theme.colors.white,
    },
    viewBoardingPassButton: {
      padding: theme.spacing(3, 4),
      backgroundColor: "transparent",
      boxShadow: "none",
      width: "100%",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      border: "none",
      "&:hover": {backgroundColor: theme.colors.gray50},
    },
  });

export default function ReservationsSection({guestId}) {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const guests_dict = useSelector((state) => state.defaultReducer.guests_dict);
  const bookings_dict = useSelector(
    (state) => state.defaultReducer.bookings_dict,
  );
  const loading = useSelector((state) => state.defaultReducer.loading);
  const [expanded, setExpanded] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [orderedBookings, setOrderedBookings] = useState([]);
  const [bookingMap, setBookingMap] = useState({
    loaded: false,
    currentBookings: [],
    upcomingBookings: [],
    pastBookings: [],
  });
  const {t} = useTranslation();

  const guest = guests_dict[guestId];

  const openBp = (booking_id) => {
    dispatch(
      getGuestKeycardLink(guest.guest_id, booking_id, (link) =>
        window.open(link, "_blank"),
      ),
    );
  };

  useLayoutEffect(() => {
    setBookingMap({
      loaded: false,
      currentBookings: [],
      upcomingBookings: [],
      pastBookings: [],
    });
  }, [guestId]);

  useEffect(() => {
    if (!guest) return;
    const newOrderedBkgs = orderBookings(guest, bookings_dict, guestId);
    setOrderedBookings(newOrderedBkgs);
  }, [bookings_dict, guest]);

  useEffect(() => {
    dispatch(setSelectedGuestBookings(orderedBookings));
  }, [orderedBookings]);

  useLayoutEffect(() => {
    let past = [];
    let current = [];
    let upcoming = [];

    let currentTime = Date.now();
    for (let booking of orderedBookings) {
      if (booking.checkout_raw < currentTime) {
        past.push(booking);
      } else if (booking.checkin_raw < currentTime) {
        current.push(booking);
      } else {
        upcoming.push(booking);
      }
    }

    const guestHasBookings =
      guest?.booking_status === "inquiry"
        ? !!guest?.inquiries?.length
        : !!guest?.bookings?.length;
    const bookingsMapped =
      !!current.length || !!upcoming.length || !!past.length;
    const bookingsLoaded =
      !loading.bookings && (!guestHasBookings || bookingsMapped);

    if (current.length) {
      setSelectedTab(0);
    } else if (upcoming.length) {
      setSelectedTab(1);
    } else if (past.length) {
      setSelectedTab(2);
    } else if (!bookingsLoaded) {
      setSelectedTab(0);
    }

    const timer = setTimeout(() => {
      setBookingMap({
        loaded: bookingsLoaded,
        currentBookings: current,
        upcomingBookings: upcoming,
        pastBookings: past,
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [orderedBookings, loading.bookings]);

  function handleBookingAction(
    props,
    action_type = null,
    onSuccess = () => null,
    booking,
  ) {
    let action =
      !!action_type && !!props[action_type]
        ? props[action_type].action_options
        : props.action_options;

    dispatch(
      createAction({
        bookingId: booking.booking_id,
        guestId: guest?.guest_id,
        action,
        onSuccess: (r) => {
          if (action.action_id === "verification_report") {
            if (isMobile) {
              window.open(
                r.booking.verification_report_url,
                "_system",
                "location=yes",
              );
            } else {
              window.open(r.booking.verification_report_url);
            }
          }

          const currGuest = guests_dict[booking.guest_id];
          if (!!currGuest) {
            if (!!r.guest_bills) {
              dispatch(editGuest({...currGuest, bills: r.guest_bills}));
            }
            if (!!r.guest_bill) {
              const newBills = (currGuest?.bills || []).map((b) =>
                b.id === r.guest_bill.id ? r.guest_bill : b,
              );
              dispatch(editGuest({...currGuest, bills: newBills}));
            }
          }
          dispatch(editBooking(r.booking));

          dispatch(getInboxData());

          onSuccess();
        },
      }),
    );
  }

  function getBkgListContent(bkgs, type) {
    if (!bookingMap.loaded)
      return (
        <Container
          key={type}
          sx={{width: "100%", height: "150px", marginTop: theme.spacing(5)}}
          skeleton={{enabled: true}}
        />
      );
    else if (!bkgs.length)
      return (
        <FlexContainer
          key={type}
          justifyContent={"center"}
          alignItems={"center"}
          flexDirection={"column"}
          gap={theme.spacing(2)}
          sx={{width: "100%", padding: theme.spacing(2, 5, 0)}}
        >
          <SvgIcon
            component={ICONS["search-people"]}
            sx={{width: "150px", height: "150px"}}
          />
          <Text
            variant="textLG"
            color={"textSecondary"}
            sx={{textAlign: "center"}}
          >
            {t("inbox.guest-details.reservations.no-reservations")}
          </Text>
          <Text
            variant="textMD"
            color={"textTertiary"}
            sx={{textAlign: "center"}}
          >
            {t("inbox.guest-details.reservations.no-reservations-detail", {
              type: type,
            })}
          </Text>
        </FlexContainer>
      );
    return (
      <FlexContainer
        key={type}
        flexDirection={"column"}
        sx={{padding: theme.spacing(4, 2, 2), width: "100%"}}
        gap={theme.spacing(4)}
      >
        {bkgs.map((bkg, index) => {
          const isInquiry =
            !!bkg?.inquiry_id || (bkg?.status || "inquiry").includes("inquiry");
          return (
            <Container
              className={classes.container}
              key={bkg.booking_id ?? `inquiry-${bkg.listing_id}-${index}`}
            >
              <ListingInfoCard booking={bkg} />
              {!isInquiry && (
                <BoardingPassCard
                  booking={bkg}
                  handleAction={(...args) => handleBookingAction(...args, bkg)}
                />
              )}

              <ActionApproveDeclineCard
                booking={bkg}
                handleAction={(...args) => handleBookingAction(...args, bkg)}
              />
              <AccessCodesCard booking={bkg} />
              <UpsellsCard booking={bkg} guest={guest} />
              <FeesCard
                booking={bkg}
                guest={guest}
                handleAction={(...args) => handleBookingAction(...args, bkg)}
              />
              <CustomFieldsCard booking={bkg} />
              {!isInquiry && (
                <ButtonIconText
                  className={classes.viewBoardingPassButton}
                  label={t(
                    "inbox.guest-details.reservations.view-boarding-pass",
                  )}
                  weight="semibold"
                  typographyVariant="textSM"
                  onClick={() => openBp(bkg.booking_id)}
                  icon={
                    <SvgIcon
                      component={ICONS.eye}
                      sx={{
                        marginRight: theme.spacing(3),
                        width: "16px",
                        height: "16px",
                        color: theme.colors.textPrimary,
                      }}
                    />
                  }
                />
              )}
            </Container>
          );
        })}
      </FlexContainer>
    );
  }

  const filteredTabs = useMemo(() => {
    let tabs = [
      {label: "Active", count: bookingMap.currentBookings.length},
      {label: "Upcoming", count: bookingMap.upcomingBookings.length},
      {label: "Past", count: bookingMap.pastBookings.length},
    ];

    let content = [
      getBkgListContent(bookingMap.currentBookings, "active"),
      getBkgListContent(bookingMap.upcomingBookings, "upcoming"),
      getBkgListContent(bookingMap.pastBookings, "past"),
    ];

    return {tabs, content};
  }, [bookingMap]);

  return (
    <DropdownExpand
      divider={false}
      expanded={expanded}
      onExpand={() => setExpanded(!expanded)}
      customLabel={
        <Text variant={"textLG"} weight="semibold">
          {t("inbox.guest-details.reservations.header")}
        </Text>
      }
    >
      <Container sx={{paddingTop: theme.spacing(1)}}>
        <Tabs
          labels={filteredTabs.tabs.map((tab) => tab.label)}
          selectedTab={selectedTab}
          onTabClick={(index) => setSelectedTab(index)}
          headerProps={{
            sx: {
              backgroundColor: theme.colors.white,
              marginBottom: 0,
            },
          }}
          tabProps={{sx: {flex: 0}}}
          customLabelType={(label, index, activeTab) => (
            <FlexContainer alignItems="center" gap={theme.spacing(1)}>
              <Text
                variant="textSM"
                weight="semibold"
                color={activeTab === index ? "primary500" : "textSecondary"}
              >
                {label}
              </Text>
              {!!filteredTabs.tabs[index].count && (
                <ChipText
                  text={filteredTabs.tabs[index].count}
                  color={activeTab === index ? "primary700" : "gray700"}
                  typographyVariant="textXS"
                  sx={{
                    backgroundColor:
                      activeTab === index
                        ? theme.colors.primary50
                        : theme.colors.gray100,
                    "&:hover": {
                      backgroundColor:
                        activeTab === index
                          ? theme.colors.primary50
                          : theme.colors.gray100,
                    },
                  }}
                />
              )}
            </FlexContainer>
          )}
        >
          {filteredTabs.content}
        </Tabs>
      </Container>
    </DropdownExpand>
  );
}
